import { useEffect, useRef, useState } from "react";
import { isNative } from "@/lib/capacitor";
import {
  cancelAll,
  scheduleTimerNotification,
  timerNotificationId,
} from "@/hooks/useTimerNotification";

interface SavedTimer {
  /** Remaining seconds when the user left the step (used when paused). */
  remaining: number;
  /** Absolute epoch-ms deadline while running, `null` when paused. */
  endsAt: number | null;
}

/**
 * Countdown timer for a single cooking step.
 *
 * - The timer is keyed by `stepKey` (step identity), not by its duration:
 *   two consecutive steps with the same `timer_seconds` get distinct timers
 *   (audit M23).
 * - A running timer stores its absolute deadline (`endsAt`). When the user
 *   leaves the step (e.g. "Pendant ce temps") and comes back, the remaining
 *   time is re-derived from the wall clock, so elapsed time is honoured and
 *   a timer that expired meanwhile rings on return (audit M22).
 * - Navigating between steps never cancels the native alarm — only an
 *   explicit pause or reset does. Each step owns its own native alarm id,
 *   so starting or pausing step B never replaces or cancels step A's alarm.
 *
 * Returns `remaining: null` when the step has no timer (`seconds === null`).
 */
export function useCookingTimer(
  seconds: number | null,
  stepKey: string | null,
): {
  remaining: number | null;
  running: boolean;
  ringing: boolean;
  start: () => void;
  pause: () => void;
  reset: () => void;
} {
  const [remaining, setRemaining] = useState<number | null>(seconds);
  const [running, setRunning] = useState(false);
  const [ringing, setRinging] = useState(false);
  const endsAtRef = useRef<number | null>(null);
  const savedByKey = useRef(new Map<string, SavedTimer>());
  const notificationId = timerNotificationId(stepKey);

  // Mirror of the latest values so the step-change cleanup can save them.
  const liveRef = useRef({ remaining, running });
  liveRef.current = { remaining, running };

  // Restore (or reset) whenever the step identity changes; save on leave.
  useEffect(() => {
    const key = stepKey;
    const store = savedByKey.current;
    const saved = key !== null ? store.get(key) : undefined;
    if (key !== null) store.delete(key);

    if (saved && seconds !== null) {
      if (saved.endsAt !== null) {
        const left = (saved.endsAt - Date.now()) / 1000;
        if (left <= 0) {
          endsAtRef.current = null;
          setRemaining(0);
          setRunning(false);
          setRinging(true);
        } else {
          endsAtRef.current = saved.endsAt;
          setRemaining(left);
          setRunning(true);
          setRinging(false);
        }
      } else {
        endsAtRef.current = null;
        setRemaining(saved.remaining);
        setRunning(false);
        setRinging(false);
      }
    } else {
      endsAtRef.current = null;
      setRemaining(seconds);
      setRunning(false);
      setRinging(false);
    }

    return () => {
      if (key === null || seconds === null) return;
      const live = liveRef.current;
      const endsAt = live.running ? endsAtRef.current : null;
      const left = live.remaining ?? seconds;
      // Only remember timers the user actually touched.
      if (endsAt !== null || left !== seconds) {
        store.set(key, { remaining: left, endsAt });
      }
    };
  }, [stepKey, seconds]);

  // Tick from the absolute deadline.
  useEffect(() => {
    if (!running) return;
    let frame = 0;
    const tick = () => {
      const endsAt = endsAtRef.current;
      if (endsAt === null) return;
      const next = Math.max((endsAt - Date.now()) / 1000, 0);
      setRemaining(next);
      if (next <= 0) {
        endsAtRef.current = null;
        setRunning(false);
        setRinging(true);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [running]);

  return {
    remaining,
    running,
    ringing,
    start: () => {
      if (remaining === null || remaining <= 0 || running) return;
      endsAtRef.current = Date.now() + remaining * 1000;
      setRinging(false);
      setRunning(true);
      if (isNative()) {
        void scheduleTimerNotification(remaining, "Minuteur", notificationId);
      }
    },
    pause: () => {
      const endsAt = endsAtRef.current;
      if (endsAt !== null) {
        setRemaining(Math.max((endsAt - Date.now()) / 1000, 0));
      }
      endsAtRef.current = null;
      setRunning(false);
      // User paused manually — cancel the pending native alarm.
      if (isNative()) {
        void cancelAll(notificationId);
      }
    },
    reset: () => {
      endsAtRef.current = null;
      setRemaining(seconds);
      setRunning(false);
      setRinging(false);
      if (isNative()) {
        void cancelAll(notificationId);
      }
    },
  };
}

export function formatTimer(seconds: number): string {
  const total = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
